<template>
  <details class="raw-json-panel" :open="defaultOpen">
    <summary class="raw-json-summary">
      <span class="raw-json-icon" aria-hidden="true">{ }</span>
      <span class="raw-json-title">{{ title }}</span>
      <span v-if="byteSize" class="raw-json-meta">{{ byteSize }}</span>
    </summary>
    <div class="raw-json-body">
      <div class="raw-json-toolbar">
        <button
          type="button"
          class="raw-json-button"
          @click="copyJson"
        >{{ copyLabel }}</button>
        <span v-if="hint" class="raw-json-hint">{{ hint }}</span>
      </div>
      <pre class="raw-json-dump">{{ formatted }}</pre>
    </div>
  </details>
</template>

<script>
// Permanent JSON inspector for the question editors. Shows the data
// the editor will persist (or just loaded), formatted and copyable.
//
// Intentional design choices:
//   - Lives in-page (not behind a modal) so authors can see the raw
//     payload side-by-side with the form as they edit.
//   - Reactive: the dump updates as the form changes, so authors can
//     watch their edits flow into the canonical shape.
//   - Used by the team to verify storage shape, especially when
//     legacy rows get migrated through edit-and-save.

export default {
  name: 'RawJsonPanel',
  props: {
    // The data object to dump. Reactive — the parent passes a
    // computed (e.g. `serializeForm()`) and the panel re-renders.
    data: { type: null, required: true },
    title: { type: String, default: 'Raw JSON' },
    hint: { type: String, default: '' },
    defaultOpen: { type: Boolean, default: false },
  },
  data() {
    return {
      copyLabel: 'Copy',
      copyTimer: null,
    };
  },
  computed: {
    formatted() {
      try {
        return JSON.stringify(this.data, null, 2);
      } catch (e) {
        return `// could not stringify: ${e && e.message ? e.message : e}`;
      }
    },
    byteSize() {
      if (!this.formatted) return '';
      // Approximate byte length without spinning up a TextEncoder.
      // Good enough for the meta hint; we just want a sense of scale.
      const len = this.formatted.length;
      if (len < 1024) return `${len} chars`;
      return `${(len / 1024).toFixed(1)} KB`;
    },
  },
  beforeUnmount() {
    if (this.copyTimer) clearTimeout(this.copyTimer);
  },
  methods: {
    async copyJson() {
      const text = this.formatted;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback path for non-HTTPS / older Safari contexts.
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        this.copyLabel = 'Copied!';
      } catch (e) {
        this.copyLabel = 'Copy failed';
      }
      if (this.copyTimer) clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        this.copyLabel = 'Copy';
        this.copyTimer = null;
      }, 1200);
    },
  },
};
</script>

<style scoped>
.raw-json-panel {
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: #fafaf7;
  margin: 1.5rem 0 0;
  overflow: hidden;
}

.raw-json-summary {
  cursor: pointer;
  list-style: none;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #5f5e5a;
  user-select: none;
  background: #f1efe8;
  transition: background 0.15s;
}
.raw-json-summary:hover { background: #ebe9e0; }
.raw-json-summary::-webkit-details-marker { display: none; }
.raw-json-summary::marker { display: none; }

.raw-json-icon {
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  padding: 2px 6px;
  color: #534AB7;
  font-weight: 600;
}

.raw-json-title {
  font-weight: 500;
  color: #2c2c2a;
}

.raw-json-meta {
  margin-left: auto;
  font-size: 11px;
  color: #888780;
  font-family: "SF Mono", Menlo, Consolas, monospace;
}

.raw-json-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.raw-json-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.raw-json-button {
  font-size: 11px;
  padding: 4px 10px;
  background: white;
  border: 0.5px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  color: #2c2c2a;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.raw-json-button:hover {
  background: #EEEDFE;
  border-color: #534AB7;
  color: #534AB7;
}

.raw-json-hint {
  font-size: 11px;
  color: #888780;
  font-style: italic;
}

.raw-json-dump {
  max-height: 360px;
  overflow: auto;
  background: white;
  border: 0.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 11px;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  line-height: 1.5;
  margin: 0;
  white-space: pre;
}
</style>
